import { Modal } from "@material-ui/core";
import { CustomIcon } from "components/icon/customIcon.component";
import { PromptModal } from "components/modal/promptModal.component";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { ZIndex } from "config/zIndex";
import type { ChangeEvent } from "react";
import {
  ClockStampFormArea,
  ClockStampInfoDate,
  ClockStampInfoTime,
  ClockStampInfoTitle,
  ClockStampLayout,
  FormContainer,
  FormStyled,
  InfoContainer,
  InputContainer,
  ModuleInactiveContainer,
  ModuleInactiveIconContainer,
  Text,
  TextInput,
} from "./clockStamp.styled";
import { ClockStampCheckInOutModal } from "./clockStampCheckInOutModal.component";
import { ClockStampChooseTimeReportModal } from "./clockStampChooseTimeReportModal.component";
import { ClockStampCreateAutoGeneratedGigModal } from "./clockStampCreateAutoGeneratedGigModal.component";
import { useClockStamp } from "./clockStamp.logic";
import { ClockStampTimeReportPanel } from "./clockStampTimeReportPanel.component";
import { NumberPad } from "./numberPad.component";

export const ClockStamp = (): JSX.Element => {
  const {
    t,
    telephoneNumber,
    setTelephoneNumber,
    userIsCheckedIn,
    keyShouldBeDisabled,
    modalState,
    today,
    time,
    timeReport,
    setTimeReport,
    workerTimeReports,
    errorText,
    isAutoGeneratedGig,
    setIsAutoGeneratedGig,
    timeReportToTimeReport,
    setTimeReportToTimeReport,
    stampResult,
    teams,
    phoneNumber,
    setPhoneNumber,
    setKeyShouldBeDisabled,
    setSimpleModalOpen,
    setTimeReportModalOpen,
    setWarningModalOpen,
    clearInputs,
    handleCheckInCheckOut,
    handleSubmitTimeReport,
    handleNumberPadInput,
    haveStampClockModule,
  } = useClockStamp();

  if (!haveStampClockModule) {
    return (
      <ModuleInactiveContainer>
        <ModuleInactiveIconContainer>
          <CustomIcon name="clock" color={Color.BurntSienna} size="100px" />
        </ModuleInactiveIconContainer>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H3}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.Oops")}
        </Text>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H4}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.ModuleInactive")}
        </Text>
        <Text
          fontFamily={FontFamily.MontserratSemiBold}
          fontSize={FontSize.H4}
          align="center"
          color={Color.MidnightBlue}
        >
          {t("StampClock.YourAdminCanActivate")}
        </Text>
      </ModuleInactiveContainer>
    );
  }

  return (
    <>
      <ClockStampLayout>
        <ClockStampFormArea>
          <FormContainer>
            <FormStyled>
              {errorText !== "" && (
                <Text
                  fontFamily={FontFamily.MontserratSemiBold}
                  fontSize={FontSize.H4}
                  color={Color.Destructive}
                >
                  {t(errorText)}
                </Text>
              )}

              <InputContainer>
                <TextInput
                  value={telephoneNumber}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setTelephoneNumber(event.target.value)
                  }
                  placeholder={t("StampClock.TelephoneNumber")}
                />
              </InputContainer>

              <NumberPad
                checkedIn={userIsCheckedIn}
                keyShouldBeDisabled={keyShouldBeDisabled}
                onNumberChange={handleNumberPadInput}
                onClickStampKey={handleCheckInCheckOut}
              />
            </FormStyled>
          </FormContainer>
        </ClockStampFormArea>

        <InfoContainer>
          <ClockStampInfoTitle>{t("StampClock.Welcome")}</ClockStampInfoTitle>
          <ClockStampInfoTime>{time}</ClockStampInfoTime>
          <ClockStampInfoDate>{today}</ClockStampInfoDate>
        </InfoContainer>
      </ClockStampLayout>

      {modalState.type === "checkInOut" && timeReport && (
        <ClockStampCheckInOutModal
          timeReport={timeReport}
          stampResult={stampResult}
          setShowModal={setSimpleModalOpen("checkInOut")}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showModal={modalState.type === "checkInOut"}
          isAutoGeneratedGig={isAutoGeneratedGig}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          handleSubmitTimeReport={handleSubmitTimeReport}
          clearInputs={clearInputs}
        />
      )}

      {modalState.type === "createAutoGeneratedGig" && (
        <ClockStampCreateAutoGeneratedGigModal
          showCreateAutoGeneratedGigModal={
            modalState.type === "createAutoGeneratedGig"
          }
          setShowCreateAutoGeneratedGigModal={setSimpleModalOpen(
            "createAutoGeneratedGig",
          )}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          setTimeReport={setTimeReport}
          setShowModal={setSimpleModalOpen("checkInOut")}
          setIsAutoGeneratedGig={setIsAutoGeneratedGig}
          clearInputs={clearInputs}
          teams={teams}
          phoneNumber={phoneNumber}
        />
      )}

      {modalState.type === "timeReport" && timeReportToTimeReport && (
        <ClockStampTimeReportPanel
          isOpen={modalState.type === "timeReport"}
          timeReport={timeReportToTimeReport}
          phoneNumber={phoneNumber}
          onBackdropClick={() => setWarningModalOpen(true)}
          onClose={() => {
            setTimeReportModalOpen(false);
            setTimeReportToTimeReport(undefined);
            setPhoneNumber("");
            setKeyShouldBeDisabled(false);
          }}
          onSubmitted={() => {
            setTimeReportModalOpen(false);
            setTimeReportToTimeReport(undefined);
            setPhoneNumber("");
            setKeyShouldBeDisabled(false);
          }}
        />
      )}

      {modalState.type === "chooseTimeReport" && (
        <ClockStampChooseTimeReportModal
          setShowChooseTimeReportModal={setSimpleModalOpen("chooseTimeReport")}
          setKeyShouldBeDisabled={setKeyShouldBeDisabled}
          showChooseTimeReportModal={modalState.type === "chooseTimeReport"}
          setTimeReport={setTimeReport}
          clearInputs={clearInputs}
          workerTimeReports={workerTimeReports}
          handleCheckInCheckOut={handleCheckInCheckOut}
          timeReport={timeReport}
        />
      )}
      <Modal
        open={modalState.type === "timeReport" && modalState.showWarningModal}
        onClose={() => {
          setWarningModalOpen(false);
        }}
        style={{ zIndex: ZIndex.modalOverlay }}
      >
        <PromptModal
          header={t("StampClock.CancelTimeReporting")}
          message={t("StampClock.AreYourSureYouDontWantToTimeReport")}
          accept={{
            label: t("General.Yes"),
            color: Color.MidnightBlue,
            callback: () => {
              setTimeReportModalOpen(false);
            },
          }}
          cancel={{
            label: t("General.No"),
            color: Color.Destructive,
            callback: () => {
              setWarningModalOpen(false);
            },
          }}
        />
      </Modal>
    </>
  );
};
